import type { AccountInfo, Configuration } from '@azure/msal-browser'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import { AuthProvider, AuthService } from '@/services/auth/auth-service'
import { AuthState } from '@/stores/auth-store'

/**
 * MSAL authentication service.
 */
export class MsalAuthService extends AuthService<AccountInfo, PublicClientApplication> {
  authProvider = AuthProvider.Msal

  private scopes = [ 'openid', import.meta.env.VITE_AZURE_AD_SCOPE_API || '' ]

  /**
   * @inheritDoc
   */
  protected user = () => this.authClient.getActiveAccount()

  private static config: Configuration = {
    auth: {
      clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || '',
      authority: import.meta.env.VITE_AZURE_AD_AUTHORITY,
      redirectUri: import.meta.env.VITE_AZURE_AD_REDIRECT_URI || '',
      postLogoutRedirectUri: import.meta.env.VITE_AZURE_AD_POST_LOGOUT_REDIRECT_URI || '',
    },
    cache: {
      cacheLocation: 'localStorage',
      /*
       * Purge the pre-v5 (msal-browser v4) cache format on first init after the upgrade
       * instead of retaining it for the default 5 days. Stale v4 entries make
       * handleRedirectPromise throw no_token_request_cache_error. See v5 migration guide.
       */
      cacheRetentionDays: 0,
    },
  }

  // Loop guard for the interactive re-auth redirect in getAccessToken (per-tab, time-boxed).
  private static readonly reauthRedirectKey = 'msal:reauth-redirect-at'

  private static readonly reauthRedirectCooldownMs = 120_000

  /*
   * Concurrent silent token requests are fine (MSAL dedupes them), but concurrent *interactive*
   * calls are not: a second acquireTokenRedirect() while one is already in flight throws
   * BrowserAuthError "interaction_in_progress" instead of InteractionRequiredAuthError, which
   * getAccessToken rethrows as an uncaught error. This is common right when the refresh token
   * expires (~24h), since several in-flight API calls hit the silent-auth failure at once.
   * Memoizing the redirect promise ensures only the first caller triggers acquireTokenRedirect;
   * the rest just await the same in-flight redirect instead of racing to start their own.
   */
  private reauthRedirectPromise: Promise<void> | null = null

  constructor() {
    super(new PublicClientApplication(MsalAuthService.config))
    this.logger.info('MsalAuthService constructor')
  }

  /**
   * @inheritDoc
   */
  async init() {
    await this.authClient.initialize()

    if (this.user() && this.accessToken) {
      return
    }

    try {
      await this.authClient.handleRedirectPromise()
    } catch (error) {
      /*
       * A stuck `interaction_in_progress` flag with no matching cached request makes
       * handleRedirectPromise throw (no_token_request_cache_error) on every load. This
       * happens when a prior interactive call (e.g. an acquireTokenPopup fallback) was
       * blocked/interrupted and never cleared its state. clearCache() removes the stray
       * MSAL keys so the app recovers to a clean unauthenticated state instead of looping.
       */
      this.logger.error('handleRedirectPromise failed, clearing stale MSAL state')
      console.error(error)
      await this.authClient.clearCache().catch(() => {})
    }

    this.handleSetAccount()
  }

  /**
   * @inheritDoc
   */
  async isAuthenticated() {
    return !!this.user()
  }

  async getAccessToken() {
    const user = this.user()
    if (!user) {
      return null
    }

    const request = {
      scopes: this.scopes,
      authority: import.meta.env.VITE_AZURE_AD_AUTHORITY,
      account: user,
      redirectUri: `${window.location.origin}/auth/redirect`,
    }

    try {
      const result = await this.authClient.acquireTokenSilent(request)

      // Silent acquisition works again — clear any pending re-auth loop guard.
      sessionStorage.removeItem(MsalAuthService.reauthRedirectKey)
      this.reauthRedirectPromise = null

      return result.accessToken
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) {
        throw error
      }

      /*
       * Another concurrent call already kicked off the interactive redirect — piggyback on it
       * instead of calling acquireTokenRedirect again (which would throw interaction_in_progress).
       */
      if (this.reauthRedirectPromise) {
        await this.reauthRedirectPromise

        return null
      }

      /*
       * The refresh token is gone/expired, so a genuine interactive sign-in is required. We use a
       * top-level redirect (a popup would be blocked without a user gesture and leave MSAL's
       * interaction state stuck). This navigates the whole tab away, so unsaved in-page state is
       * lost — acceptable because it only happens once the refresh token has fully expired (~24h),
       * not on ordinary access-token renewal (acquireTokenSilent handles that via the refresh
       * token, no interaction). The sessionStorage marker prevents an infinite redirect loop if
       * we return from the redirect and silent acquisition still fails.
       */
      const lastRedirectAt = Number(sessionStorage.getItem(MsalAuthService.reauthRedirectKey)) || 0
      if (lastRedirectAt && Date.now() - lastRedirectAt < MsalAuthService.reauthRedirectCooldownMs) {
        sessionStorage.removeItem(MsalAuthService.reauthRedirectKey)
        this.logger.error('Silent token still failing right after interactive re-auth; not redirecting again')

        return null
      }

      sessionStorage.setItem(MsalAuthService.reauthRedirectKey, String(Date.now()))
      this.reauthRedirectPromise = this.authClient.acquireTokenRedirect(request).catch(() => {})
      await this.reauthRedirectPromise

      return null
    }
  }

  /**
   * @inheritDoc
   */
  async logout() {
    if (!this.user()) {
      return false
    }
    /*
     * clear local storage instead of logoutRedirect, otherwise user will sign out from all apps
     * await this.authClient.logoutRedirect()
     */

    localStorage.clear()

    this.accessToken = null

    return true
  }

  private handleSetAccount() {
    const accounts = this.authClient.getAllAccounts()
    if (accounts.length > 0) {
      this.authClient.setActiveAccount(accounts[accounts.length - 1])
    }

    this.accessToken = null
  }

  async loginWithToken(loginHint: string): Promise<boolean> {
    if (this.user()) {
      return true
    }

    const validationResult = MsalAuthService.validateToken(loginHint)
    if (validationResult !== true) {
      return false
    }

    /*
     * The admin portal and this portal share the same Entra app registration, so instead of
     * importing a forwarded token into the cache (which only seeds an id_token and then forces
     * a hidden-iframe renewal that Firefox/Safari block) admin forwards just its username as a
     * login hint and we do a top-level redirect. The internal employee already has a first-party
     * Entra session, so this round-trips seamlessly without a prompt in every browser. This
     * navigates away; the redirect response is processed by handleRedirectPromise() in init() on
     * return, after which navigateToLoginRequestUrl sends the browser back to the originally
     * requested portal page.
     */
    await this.authClient.acquireTokenRedirect({
      scopes: this.scopes,
      authority: import.meta.env.VITE_AZURE_AD_AUTHORITY,
      redirectUri: `${window.location.origin}/auth/redirect`,
      loginHint,
    })

    return true
  }

  /**
   * The forwarded value is just a username used as an MSAL login hint (not a credential), so
   * validation is a sanity check that it looks like one, not a JWT decode.
   */
  static validateToken(loginHint: string): AuthState | true {
    if (!loginHint || !loginHint.includes('@')) {
      console.error('Invalid login hint forwarded from admin portal')

      return AuthState.InvalidToken
    }

    return true
  }
}
